import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Box, Button, Typography, Paper } from '@mui/material';
import { supabase } from './supabaseClient'; // supabaseClient import
import { AuthProvider, useAuth } from './components/AuthProvider'; // AuthProvider import 추가, useAuth 추가
import AdminLayout from './components/layouts/AdminLayout';
import SellerLayout from './components/layouts/SellerLayout';
import LoginPage from './pages/LoginPage';
import AdminDashboard from './pages/admin/Dashboard';
import AdminProducts from './pages/admin/Products';
import AdminOrders from './pages/admin/Orders';
import Settlement from './pages/admin/Settlement';
import BrandSettlement from './pages/admin/BrandSettlement';
import RefundManagement from './pages/admin/RefundManagement';
import SellerManagement from './pages/admin/SellerManagement';
import SellerDashboard from './pages/seller/Dashboard';
import SellerProducts from './pages/seller/Products';
import SellerOrders from './pages/seller/Orders';
import SellerAnalytics from './pages/seller/Analytics';
import SellerContents from './pages/seller/Contents';
import AddContents from './pages/seller/AddContents';
import AddProducts from './pages/seller/AddProducts';
import Shop from './pages/seller/Shop';
import SellerRefundManagement from './pages/seller/RefundManagement';
import SellerSettlement from './pages/seller/Settlement';
import SellerProfile from './pages/seller/Profile';
import SellerRegister from './pages/SellerRegister';
import SellerPending from './pages/SellerPending';
import MainPage from './pages/main';
import ServiceTerms from './pages/terms/ServiceTerms';
// Affiliate System Pages
import AffiliatePartners from './pages/seller/AffiliatePartners';
import ProductAffiliateSettings from './pages/seller/ProductAffiliateSettings';
import MyBrandPartners from './pages/seller/MyBrandPartners';
import MyAffiliateEarnings from './pages/seller/MyAffiliateEarnings';
import AdminAffiliateManagement from './pages/admin/AffiliateManagement';
import FeedbackManagement from './pages/admin/FeedbackManagement';
// Affiliate Marketplace Pages
import AffiliateMarketplace from './pages/seller/AffiliateMarketplace';
import CreatorDiscovery from './pages/seller/CreatorDiscovery';
import CreatorProfile from './pages/seller/CreatorProfile';
import AffiliateWishlist from './pages/seller/AffiliateWishlist';
import AffiliateRequests from './pages/seller/AffiliateRequests';
// Share2DM
import Share2dmCampaigns from './pages/share2dm/Campaigns';
// Creator Pages
import CreatorLayout from './components/layouts/CreatorLayout';
import CreatorDashboard from './pages/creator/Dashboard';
import CreatorAnalytics from './pages/creator/Analytics';
import CreatorProfilePage from './pages/creator/Profile';
import CreatorContents from './pages/seller/Contents';
import CreatorEarnings from './pages/seller/MyAffiliateEarnings';
import CreatorMarketplace from './pages/seller/AffiliateMarketplace';
import CreatorWishlist from './pages/seller/AffiliateWishlist';
import CreatorRequests from './pages/seller/AffiliateRequests';
import CreatorBrandPartners from './pages/seller/MyBrandPartners';
import PrivacyPolicy from './pages/terms/PrivacyPolicy';
import EcommerceTerms from './pages/terms/EcommerceTerms';
import RefundPolicy from './pages/terms/RefundPolicy';
import LocationTerms from './pages/terms/LocationTerms';
import MarketingConsent from './pages/terms/MarketingConsent';
import SellerTerms from './pages/terms/SellerTerms';
import Share2dmConnect from './pages/auth/Share2dmConnect';
import AuthCallback from './pages/AuthCallback';

// PermissionDeniedModal props 타입 명시
interface PermissionDeniedModalProps {
  open: boolean;
  onHome: () => void;
  onApply: () => void;
}

function PermissionDeniedModal({ open, onHome, onApply }: PermissionDeniedModalProps) {
  if (!open) return null;
  return (
    <Box sx={{
      position: 'fixed',
      zIndex: 2000,
      top: 0, left: 0, width: '100vw', height: '100vh',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      backdropFilter: 'blur(6px)',
      background: 'rgba(0,0,0,0.15)',
    }}>
      <Paper elevation={3} sx={{ p: 5, borderRadius: 3, minWidth: 380, maxWidth: '90vw', textAlign: 'center' }}>
        <Typography variant="h5" fontWeight={700} mb={2}>
          프로필 등록이 필요합니다
        </Typography>
        <Typography sx={{ mb: 3, color: '#444', fontSize: 17 }}>
          브랜드 또는 크리에이터로 등록 후 이용할 수 있습니다.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 2 }}>
          <Button variant="contained" sx={{ minWidth: 120, fontWeight: 700, bgcolor: '#111', '&:hover': { bgcolor: '#333' } }} onClick={onApply}>
            시작하기
          </Button>
          <Button variant="outlined" color="inherit" sx={{ minWidth: 120, fontWeight: 700 }} onClick={onHome}>
            홈으로
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}

// 권한 체크 훅 (AuthProvider 사용)
function useAuthGuard(allowedRole: 'admin' | 'seller' | 'creator') {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, session, loading } = useAuth();
  const [denied, setDenied] = useState(false);
  const [noRole, setNoRole] = useState(false);
  
  useEffect(() => {
    let cancelled = false;

    const checkRole = async () => {
      console.log('🔄 Auth 상태:', { loading, user: !!user, session: !!session, location: location.pathname });
      if (loading) return;

      if (!session || !user) {
        // 세션이 없으면 잠시 대기 후 재확인 (토큰 갱신 중 일시적 SIGNED_OUT 대응)
        console.log('⏳ 세션 없음 - 1.5초 후 재확인...');
        await new Promise(resolve => setTimeout(resolve, 1500));
        if (cancelled) return;

        const { data: { session: retrySession } } = await supabase.auth.getSession();
        if (!retrySession?.user) {
          console.log('❌ 인증 최종 실패 - 로그인 페이지로 이동');
          navigate('/login');
          return;
        }

        // 세션 복구됨 - 이 시점에서는 AuthProvider가 상태를 업데이트할 것이므로 return
        console.log('✅ 세션 복구 확인 - AuthProvider 상태 업데이트 대기');
        return;
      }

      // 프로필 조회 재시도 로직 (세션 확립 타이밍 이슈 대응)
      let profile = null;
      let lastError = null;
      const maxRetries = 3;

      for (let attempt = 0; attempt < maxRetries; attempt++) {
        if (cancelled) return;

        if (attempt > 0) {
          console.log(`🔄 프로필 조회 재시도 ${attempt + 1}/${maxRetries}...`);
          await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
        }

        const { data, error } = await supabase
          .from('profiles')
          .select('role')
          .eq('id', user.id)
          .single();

        if (!error && data) {
          profile = data;
          lastError = null;
          break;
        }

        lastError = error;
        console.warn(`⚠️ 프로필 조회 시도 ${attempt + 1} 실패:`, error);
      }

      if (cancelled) return;

      console.log('🔍 권한 체크:', {
        'user.id': user?.id,
        'session': !!session,
        'profile.role': profile?.role,
        'allowedRole': allowedRole,
        'location': location.pathname,
        'profile_error': lastError
      });

      if (lastError) {
        console.error('❌ Profile 조회 최종 실패:', lastError);
        alert('인증에 문제가 발생했습니다. 다시 로그인해주세요.');
        await supabase.auth.signOut();
        navigate('/login');
        return;
      }

      if (!profile?.role) {
        setNoRole(true);
        await supabase.auth.signOut();
        return;
      }

      const allowed = profile.role === allowedRole;
      if (!allowed) {
        setDenied(true);
      }
    };

    checkRole();

    return () => { cancelled = true; };
  }, [allowedRole, location.pathname, navigate, user, session, loading]);
  
  const handleHome = () => { window.location.href = '/'; };
  const handleApply = () => { window.location.href = '/register'; };

  if (loading) {
    return <div>로딩 중...</div>;
  }

  if (noRole) {
    return <PermissionDeniedModal open onHome={handleHome} onApply={handleApply} />;
  }

  return denied ? <PermissionDeniedModal open onHome={handleHome} onApply={handleApply} /> : null;
}

// 메인 페이지도 AuthProvider 사용
function MainGuardedPage() {
  const { user, session, loading } = useAuth();
  const [show, setShow] = useState(false);
  const [noRole, setNoRole] = useState(false);
  const navigate = useNavigate();
  
  useEffect(() => {
    const check = async () => {
      if (loading) return;
      
      // 로그인하지 않은 사용자는 메인 페이지 표시
      if (!session || !user) {
        setShow(true);
        return;
      }
      
      // 로그인한 사용자는 role 체크
      const { data: profile } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', user.id)
        .single();
        
      if (!profile?.role) {
        setNoRole(true);
        await supabase.auth.signOut();
        return;
      }
      
      // role이 있는 사용자는 메인 페이지 표시하지 않음 (다른 페이지로 리다이렉트됨)
      setShow(false);
    };
    
    check();
  }, [user, session, loading]);
  
  const handleHome = () => { window.location.href = '/'; };
  const handleApply = () => { window.location.href = '/register'; };

  if (loading) {
    return <div>로딩 중...</div>;
  }

  if (noRole) {
    return <PermissionDeniedModal open onHome={handleHome} onApply={handleApply} />;
  }

  return show ? <MainPage /> : null;
}

// Seller Register 권한 체크 훅
function useSellerRegisterGuard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, session, loading } = useAuth();
  const [denied, setDenied] = useState(false);
  
  useEffect(() => {
    const checkRole = async () => {
      if (loading) return;
      
      if (!session || !user) {
        navigate('/login');
        return;
      }
      
      const { data: profile } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', user.id)
        .single();
        
      console.log('SellerRegister - profile.role:', profile?.role);
      
      // user role이 아니면 접근 거부 (신규 유저만 접근)
      if (profile?.role !== 'user') {
        if (profile?.role === 'seller') navigate('/seller');
        else if (profile?.role === 'creator') navigate('/creator');
        else if (profile?.role === 'admin') navigate('/admin');
        else setDenied(true);
      }
    };
    
    checkRole();
  }, [location.pathname, navigate, user, session, loading]);
  
  const handleHome = () => { window.location.href = '/'; };
  const handleApply = () => { window.location.href = '/register'; };

  if (loading) {
    return <div>로딩 중...</div>;
  }

  return denied ? <PermissionDeniedModal open onHome={handleHome} onApply={handleApply} /> : null;
}

// Seller Register Layout에 권한 체크 적용
function SellerRegisterGuardedPage() {
  const modal = useSellerRegisterGuard();
  return <>{modal}<SellerRegister /></>;
}

// Admin/Seller Layout에 권한 체크 적용
function AdminGuardedLayout() {
  const modal = useAuthGuard('admin');
  return <>{modal}<AdminLayout /></>;
}
function SellerGuardedLayout() {
  const modal = useAuthGuard('seller');
  return <>{modal}<SellerLayout /></>;
}
function CreatorGuardedLayout() {
  const modal = useAuthGuard('creator');
  return <>{modal}<CreatorLayout /></>;
}

function App() {
  const navigate = useNavigate();
  const { user, session, loading } = useAuth();

  // 로그인 후 초기 리다이렉트만 처리 (한 번만)
  useEffect(() => {
    if (!loading && session?.user) {
      const checkInitialRole = async () => {
        const { data: profile, error } = await supabase
          .from('profiles')
          .select('role')
          .eq('id', session.user.id)
          .single();

        if (error) {
          console.error('Error fetching profile:', error);
          return;
        }

        // 현재 경로가 루트이고 role이 있으면 해당 페이지로 리다이렉트
        if (window.location.pathname === '/' && profile?.role) {
          if (profile.role === 'admin') {
            navigate('/admin');
          } else if (profile.role === 'seller') {
            navigate('/seller');
          } else if (profile.role === 'creator') {
            navigate('/creator');
          } else if (profile.role === 'user') {
            navigate('/register');
          }
        }
      };
      
      checkInitialRole();
    }
  }, [session, loading, navigate]);

  return (
    <Routes>
      {/* 루트는 로그인으로 리다이렉트 */}
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="/login" element={<LoginPage />} />

      {/* CRM Routes - MUI Box로 감싸기 */}
      <Route path="/admin" element={<Box sx={{ display: 'flex', minHeight: '100vh' }}><AdminGuardedLayout /></Box>}>
        <Route index element={<AdminDashboard />} />
        <Route path="products" element={<AdminProducts />} />
        <Route path="orders" element={<AdminOrders />} />
        <Route path="settlement" element={<Settlement />} />
        <Route path="brand-settlement" element={<BrandSettlement />} />
        <Route path="refund-management" element={<RefundManagement />} />
        <Route path="seller-management" element={<SellerManagement />} />
        <Route path="affiliate-management" element={<AdminAffiliateManagement />} />
        <Route path="feedback" element={<FeedbackManagement />} />
      </Route>

      <Route path="/seller" element={<Box sx={{ display: 'flex', minHeight: '100vh' }}><SellerGuardedLayout /></Box>}>
        <Route index element={<SellerDashboard />} />
        <Route path="products" element={<SellerProducts />} />
        <Route path="shop" element={<Shop />} />
        <Route path="orders" element={<SellerOrders />} />
        <Route path="analytics" element={<SellerAnalytics />} />
        <Route path="contents" element={<SellerContents />} />
        <Route path="addcontents" element={<AddContents />} />
        <Route path="addcontents/:contentId" element={<AddContents />} />
        <Route path="addproducts" element={<AddProducts />} />
        <Route path="refunds" element={<SellerRefundManagement />} />
        <Route path="settlement" element={<SellerSettlement />} />
        <Route path="profile" element={<SellerProfile />} />
        <Route path="affiliate-partners" element={<AffiliatePartners />} />
        <Route path="product-affiliate-settings" element={<ProductAffiliateSettings />} />
        <Route path="product-affiliate-settings/:productId" element={<ProductAffiliateSettings />} />
        <Route path="my-brand-partners" element={<MyBrandPartners />} />
        <Route path="my-affiliate-earnings" element={<MyAffiliateEarnings />} />
        {/* Affiliate Marketplace Routes */}
        <Route path="affiliate-marketplace" element={<AffiliateMarketplace />} />
        <Route path="creator-discovery" element={<CreatorDiscovery />} />
        <Route path="creator-profile/:creatorId" element={<CreatorProfile />} />
        <Route path="affiliate-wishlist" element={<AffiliateWishlist />} />
        <Route path="affiliate-requests" element={<AffiliateRequests />} />
        <Route path="share2dm" element={<Share2dmCampaigns />} />
      </Route>

      <Route path="/creator" element={<Box sx={{ display: 'flex', minHeight: '100vh' }}><CreatorGuardedLayout /></Box>}>
        <Route index element={<CreatorDashboard />} />
        <Route path="contents" element={<CreatorContents />} />
        <Route path="addcontents" element={<AddContents />} />
        <Route path="addcontents/:contentId" element={<AddContents />} />
        <Route path="analytics" element={<CreatorAnalytics />} />
        <Route path="earnings" element={<CreatorEarnings />} />
        <Route path="profile" element={<CreatorProfilePage />} />
        <Route path="marketplace" element={<CreatorMarketplace />} />
        <Route path="wishlist" element={<CreatorWishlist />} />
        <Route path="requests" element={<CreatorRequests />} />
        <Route path="brand-partners" element={<CreatorBrandPartners />} />
        <Route path="share2dm" element={<Share2dmCampaigns />} />
      </Route>

      <Route path="/register" element={<SellerRegisterGuardedPage />} />
      <Route path="/seller-pending" element={<SellerPending />} />

      {/* OAuth 콜백 — 로그인 가드 없음 */}
      <Route path="/auth/callback" element={<AuthCallback />} />
      {/* share2dm 연결 인증 페이지 — 로그인 가드 없음 (자체 처리) */}
      <Route path="/auth/share2dm-connect" element={<Share2dmConnect />} />

      {/* Terms Routes */}
      <Route path="/terms/service" element={<ServiceTerms />} />
      <Route path="/terms/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms/ecommerce" element={<EcommerceTerms />} />
      <Route path="/terms/refund" element={<RefundPolicy />} />
      <Route path="/terms/location" element={<LocationTerms />} />
      <Route path="/terms/marketing" element={<MarketingConsent />} />
      <Route path="/terms/seller" element={<SellerTerms />} />
    </Routes>
  );
}

// AuthProvider로 감싸는 래퍼 컴포넌트
function AppWrapper() {
  return (
    <AuthProvider>
      <App />
    </AuthProvider>
  );
}

export default AppWrapper; 